import React, { useState } from 'react';
import { Clock, Calendar, Pin, ArrowUpRight } from 'lucide-react';
import { NewsArticle } from '../../types/news';

interface NewsCardProps {
  article: NewsArticle;
  isFr: boolean;
  isPinned?: boolean;
  onClick?: (article: NewsArticle) => void;
  className?: string;
}

export const NewsCard: React.FC<NewsCardProps> = ({
  article,
  isFr,
  isPinned = false,
  onClick,
  className = '',
}) => {
  const [imageError, setImageError] = useState(false);

  const title = isFr ? article.title_fr || article.title_en : article.title_en || article.title_fr;
  const excerpt = isFr ? article.excerpt_fr || article.excerpt_en : article.excerpt_en || article.excerpt_fr;
  const category = isFr ? article.category_fr || article.category : article.category || article.category_fr;
  const readingTime = article.reading_time_minutes || 4;

  const formattedDate = React.useMemo(() => {
    try {
      const date = new Date(article.published_at);
      return date.toLocaleDateString(isFr ? 'fr-FR' : 'en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return article.published_at;
    }
  }, [article.published_at, isFr]);

  const fallbackImage = 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=800&q=80';

  return (
    <article
      onClick={() => onClick && onClick(article)}
      className={`group bg-white rounded-3xl border border-slate-200/80 shadow-xs hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden cursor-pointer relative ${
        isPinned ? 'ring-2 ring-emerald-600/40' : ''
      } ${className}`}
    >
      {/* 1. Thumbnail Image Container */}
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
        <img
          src={imageError ? fallbackImage : article.image_url || fallbackImage}
          alt={title}
          loading="lazy"
          decoding="async"
          onError={() => setImageError(true)}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
        />

        {/* Gradient Overlay for Text Readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent pointer-events-none" />

        {/* Category Chip */}
        <div className="absolute top-3.5 left-3.5 flex items-center gap-1.5 z-10">
          <span className="px-3 py-1 rounded-full text-[11px] font-bold tracking-wide uppercase bg-white/95 text-slate-800 shadow-sm backdrop-blur-xs border border-white/40">
            {category}
          </span>
        </div>

        {/* Pinned Badge */}
        {isPinned && (
          <div className="absolute top-3.5 right-3.5 z-10">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-600 text-white shadow-md">
              <Pin className="w-3 h-3 fill-current" />
              <span>{isFr ? 'À la une' : 'Pinned'}</span>
            </span>
          </div>
        )}
      </div>

      {/* 2. Content Body */}
      <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
        <div className="space-y-2">
          {/* Metadata: Date & Reading Time */}
          <div className="flex items-center gap-3 text-[11px] font-mono text-slate-400">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5 text-slate-400" />
              <time dateTime={article.published_at}>{formattedDate}</time>
            </span>
            <span>•</span>
            <span className="flex items-center gap-1 text-slate-500 font-medium">
              <Clock className="w-3.5 h-3.5 text-slate-400" />
              <span>{readingTime} {isFr ? 'min de lecture' : 'min read'}</span>
            </span>
          </div>

          {/* Title */}
          <h3 className="font-serif font-black text-slate-900 text-base leading-snug line-clamp-2 group-hover:text-emerald-700 transition-colors duration-200">
            {title}
          </h3>

          {/* Excerpt */}
          {excerpt && (
            <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">
              {excerpt}
            </p>
          )}
        </div>

        {/* Read More Link */}
        <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-emerald-800 group-hover:text-emerald-950">
          <span>{isFr ? 'Lire l’article' : 'Read article'}</span>
          <ArrowUpRight className="w-4 h-4 transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </div>
      </div>
    </article>
  );
};
